// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: recurring events. The shape mirrors Microsoft Graph's
// patternedRecurrence one to one so it can go on the wire untouched; the
// helpers here turn it into the small set of choices the form offers and
// back, and into a sentence for chips and tooltips.

export type RecurrencePatternType = 'daily' | 'weekly' | 'absoluteMonthly' | 'relativeMonthly' | 'absoluteYearly';
export type RecurrenceRangeType = 'noEnd' | 'endDate' | 'numbered';
export type RecurrenceWeekday = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';

export interface KonvoRecurrence {
	pattern: {
		type: RecurrencePatternType;
		interval: number;
		daysOfWeek?: Array<RecurrenceWeekday>;
		dayOfMonth?: number;
		month?: number;
		firstDayOfWeek?: RecurrenceWeekday;
	};
	range: {
		type: RecurrenceRangeType;
		/** YYYY-MM-DD, the first day the pattern applies. */
		startDate: string;
		endDate?: string;
		numberOfOccurrences?: number;
	};
}

/** What the "Repeats" control offers. Custom covers anything the presets cannot express. */
export type RecurrencePreset = 'none' | 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'yearly';

export const RECURRENCE_WEEKDAYS: ReadonlyArray<RecurrenceWeekday> = [
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
	'sunday',
];

const WORK_WEEK: ReadonlyArray<RecurrenceWeekday> = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
const JS_DAY_TO_WEEKDAY: ReadonlyArray<RecurrenceWeekday> = [
	'sunday',
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
];

/** The calendar day (YYYY-MM-DD) of a local date-time input value. */
export function dateOnly(localInput: string): string {
	return localInput.slice(0, 10);
}

export function weekdayOf(dateIso: string): RecurrenceWeekday {
	const parsed = new Date(`${dateIso.slice(0, 10)}T12:00:00`);
	return JS_DAY_TO_WEEKDAY[Number.isNaN(parsed.getTime()) ? 1 : parsed.getDay()] ?? 'monday';
}

export function dayOfMonthOf(dateIso: string): number {
	const day = Number(dateIso.slice(8, 10));
	return Number.isFinite(day) && day >= 1 ? day : 1;
}

export interface RecurrenceChoice {
	preset: RecurrencePreset;
	interval: number;
	daysOfWeek: Array<RecurrenceWeekday>;
	rangeType: RecurrenceRangeType;
	endDate: string;
	occurrences: number;
}

export function defaultRecurrenceChoice(startDate: string): RecurrenceChoice {
	return {
		preset: 'none',
		interval: 1,
		daysOfWeek: [weekdayOf(startDate)],
		rangeType: 'noEnd',
		endDate: '',
		occurrences: 10,
	};
}

function clampInt(value: number, min: number, max: number, fallback: number): number {
	if (!Number.isFinite(value)) return fallback;
	return Math.min(max, Math.max(min, Math.round(value)));
}

/** Build the wire shape from the form's choice; null when the event does not repeat. */
export function buildRecurrence(choice: RecurrenceChoice, startDate: string): KonvoRecurrence | null {
	if (choice.preset === 'none') return null;
	const interval = clampInt(choice.interval, 1, 99, 1);
	const day = startDate.slice(0, 10);
	let pattern: KonvoRecurrence['pattern'];
	switch (choice.preset) {
		case 'daily':
			pattern = {type: 'daily', interval};
			break;
		case 'weekdays':
			pattern = {type: 'weekly', interval: 1, daysOfWeek: [...WORK_WEEK], firstDayOfWeek: 'monday'};
			break;
		case 'weekly': {
			const days = RECURRENCE_WEEKDAYS.filter((weekday) => choice.daysOfWeek.includes(weekday));
			pattern = {
				type: 'weekly',
				interval,
				daysOfWeek: days.length > 0 ? days : [weekdayOf(day)],
				firstDayOfWeek: 'monday',
			};
			break;
		}
		case 'monthly':
			pattern = {type: 'absoluteMonthly', interval, dayOfMonth: dayOfMonthOf(day)};
			break;
		case 'yearly':
			pattern = {type: 'absoluteYearly', interval, dayOfMonth: dayOfMonthOf(day), month: Number(day.slice(5, 7))};
			break;
	}
	let range: KonvoRecurrence['range'];
	if (choice.rangeType === 'endDate' && choice.endDate) {
		range = {type: 'endDate', startDate: day, endDate: choice.endDate.slice(0, 10)};
	} else if (choice.rangeType === 'numbered') {
		range = {type: 'numbered', startDate: day, numberOfOccurrences: clampInt(choice.occurrences, 1, 999, 10)};
	} else {
		range = {type: 'noEnd', startDate: day};
	}
	return {pattern, range};
}

/** Read a Graph recurrence back into the form's choice. Custom shapes fall back to the closest preset. */
export function choiceFromRecurrence(
	recurrence: KonvoRecurrence | null | undefined,
	startDate: string,
): RecurrenceChoice {
	const base = defaultRecurrenceChoice(startDate);
	if (!recurrence) return base;
	const {pattern, range} = recurrence;
	const days = (pattern.daysOfWeek ?? []).filter((weekday): weekday is RecurrenceWeekday =>
		RECURRENCE_WEEKDAYS.includes(weekday),
	);
	let preset: RecurrencePreset = 'daily';
	if (pattern.type === 'daily') preset = 'daily';
	else if (pattern.type === 'weekly') {
		const isWorkWeek =
			pattern.interval === 1 && days.length === 5 && WORK_WEEK.every((weekday) => days.includes(weekday));
		preset = isWorkWeek ? 'weekdays' : 'weekly';
	} else if (pattern.type === 'absoluteMonthly' || pattern.type === 'relativeMonthly') preset = 'monthly';
	else if (pattern.type === 'absoluteYearly') preset = 'yearly';
	return {
		preset,
		interval: clampInt(pattern.interval, 1, 99, 1),
		daysOfWeek: days.length > 0 ? days : base.daysOfWeek,
		rangeType: range.type === 'endDate' || range.type === 'numbered' ? range.type : 'noEnd',
		endDate: range.endDate ?? '',
		occurrences: clampInt(range.numberOfOccurrences ?? 10, 1, 999, 10),
	};
}

export function sameRecurrence(a: KonvoRecurrence | null, b: KonvoRecurrence | null): boolean {
	if (a === b) return true;
	if (!a || !b) return false;
	return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
}

function normalize(recurrence: KonvoRecurrence): KonvoRecurrence {
	return {
		pattern: {
			type: recurrence.pattern.type,
			interval: recurrence.pattern.interval,
			daysOfWeek: recurrence.pattern.daysOfWeek
				? RECURRENCE_WEEKDAYS.filter((weekday) => recurrence.pattern.daysOfWeek?.includes(weekday))
				: undefined,
			dayOfMonth: recurrence.pattern.dayOfMonth,
			month: recurrence.pattern.month,
		},
		range: {
			type: recurrence.range.type,
			startDate: recurrence.range.startDate.slice(0, 10),
			endDate: recurrence.range.endDate?.slice(0, 10),
			numberOfOccurrences: recurrence.range.numberOfOccurrences,
		},
	};
}

export interface RecurrenceSummaryLabels {
	daily: (interval: number) => string;
	weekdays: string;
	weekly: (interval: number, days: string) => string;
	monthly: (interval: number, day: number) => string;
	yearly: (interval: number) => string;
	until: (date: string) => string;
	times: (count: number) => string;
	weekday: (weekday: RecurrenceWeekday) => string;
}

/** "Every 2 weeks on Mon, Wed · until 30 Nov". Labels come from the caller so lingui owns the words. */
export function recurrenceSummary(recurrence: KonvoRecurrence, labels: RecurrenceSummaryLabels): string {
	const {pattern, range} = recurrence;
	const choice = choiceFromRecurrence(recurrence, range.startDate);
	let head: string;
	switch (choice.preset) {
		case 'weekdays':
			head = labels.weekdays;
			break;
		case 'weekly':
			head = labels.weekly(pattern.interval, choice.daysOfWeek.map(labels.weekday).join(', '));
			break;
		case 'monthly':
			head = labels.monthly(pattern.interval, pattern.dayOfMonth ?? dayOfMonthOf(range.startDate));
			break;
		case 'yearly':
			head = labels.yearly(pattern.interval);
			break;
		default:
			head = labels.daily(pattern.interval);
	}
	if (range.type === 'endDate' && range.endDate) return `${head} · ${labels.until(range.endDate)}`;
	if (range.type === 'numbered' && range.numberOfOccurrences)
		return `${head} · ${labels.times(range.numberOfOccurrences)}`;
	return head;
}

export type KonvoEventSeriesType = 'singleInstance' | 'occurrence' | 'exception' | 'seriesMaster';

export function isSeriesEvent(type: KonvoEventSeriesType | null | undefined): boolean {
	return type === 'occurrence' || type === 'exception' || type === 'seriesMaster';
}
